import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { IconButton, Stack, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../shared/contexts/app-context/components/AppContext";
import { PortfolioItemUpdateModel } from "../../../../features/portfolio-info/types/PortfolioItemUpdateModel";
import {
    useActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { dateTimeConversionService } from "../../../../shared/services/DateTimeConversionService";
import { assetService } from "../../../../shared/services/AssetService";
import {
    ActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { AssetType } from "../../../../shared/types/AssetType";
import { OrderState } from "../../../../shared/types/OrderState";
import { DraggableDialogPaper } from "../DraggableDialogPaper";
import { DraggableDialogTitle } from "../DraggableDialogTitle";
import { ContractDetails, ContractDetailsState } from "./controls/ContractDetails";
import { PricingDetailsState, PricingParameters } from "./controls/PricingParameters";
import { OrderDetails, OrderDetailsState } from "./controls/OrderDetails";
import { BinanceOrderManager } from "./controls/BinanceOrderManager";
import { OptionDeltaTrend } from "./controls/OptionDeltaTrend";
import { oderManagerUtils } from "./utils/OrderManagerUtils";
import { AutoOrderManagerDialog } from "../auto-order-manager-dialog/AutoOrderManagerDialog";
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AutoOrderConfigurationModel } from "../../../../features/portfolio-info/types/AutoOrderConfigurationModel";
import { AutoOrderConfigurationDto } from "../../../../features/portfolio-info/types/AutoOrderConfigurationDto";
import { LocalStorageConstants } from "../../../../shared/constants/LocalStorageConstants";
import { DataProviders } from "../../../../shared/types/DataProviders";
import { useAuthContext } from "../../../../features/authentication/hooks/use-auth/AuthContext";
import type { MarketDataDto } from "../../../../shared/types/MarketDataDto";

export function OrderManagerDialog({
    isOpen,
    setIsOpen,
    submitAction,
    deleteAction,
    editingModel,
    isNew,
    icon,
    onAutoOrderConfigurationSaved,
    saveAutoOrderConfiguration,
    fetchAutoOrderConfiguration,
    resetAutoOrderConfigurationState,
    reloadEditingOrder,
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    submitAction: ((portfolio: PortfolioItemUpdateModel) => Promise<void>) | undefined,
    deleteAction: () => void,
    editingModel: PortfolioItemUpdateModel | null,
    isNew: boolean,
    icon: React.ReactNode;
    onAutoOrderConfigurationSaved: (configuration: AutoOrderConfigurationModel) => void;
    saveAutoOrderConfiguration: (
        portfolioItemId: number,
        autoOrderConfiguration: AutoOrderConfigurationDto,
    ) => Promise<AutoOrderConfigurationModel | null>;
    fetchAutoOrderConfiguration: (portfolioItemId: number) => Promise<AutoOrderConfigurationModel | null>;
    resetAutoOrderConfigurationState: (portfolioItemId: number) => Promise<AutoOrderConfigurationModel | null>;
    reloadEditingOrder: (marketData: MarketDataDto | null) => Promise<PortfolioItemUpdateModel | null>;
}) {
    const {
        marketDataDto,
        fetchMarketData,
        setSelectedDataProvider,
    } = useAppContext();

    const { isUserAuthenticated } = useAuthContext();
    const navigate = useNavigate();

    const [isAutoOrderManagerDialogOpen, setIsAutoOrderManagerDialogOpen] = useState<boolean>(false);
    const [isDataUpdating, setIsDataUpdating] = useState<boolean>(false);
    
    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();
    
    const [contractDetails, setContractDetails] = useState<ContractDetailsState>({
        assetType: AssetType.Spot,
        expiration: '',
        strike: null,
        symbol: '',
    });

    const handleContractDetailsChange = (patch: Partial<ContractDetailsState>) => {
        setContractDetails(prev => ({ ...prev, ...patch }));
    };

    const [pricingDetails, setPricingDetails] = useState<PricingDetailsState>({
        dateForPricing: dayjs(),
        impliedVolatilityForPricing: 0,
        underlyingPriceForPricing: 0,
        theoreticalPrice: 0,
        delta: 0,
        gamma: 0,
        vega: 0,
        theta: 0,
    });

    const handlePricingDetailsChange = (patch: Partial<PricingDetailsState>) => {
        setPricingDetails(prev => ({ ...prev, ...patch }));
    };
    
    const [orderDetails, setOrderDetails] = useState<OrderDetailsState>({
        externalId: 0,
        state: OrderState.Disabled,
        isActive: false, // obsolete
        archived: false, // obsolete
        executedAt: dayjs(dateTimeConversionService.getCurrentUtcDate()),
        underlyingPriceAtExecution: 0,
        impliedVolatilityAtExecution: 0,
        exchangeOrderId: null,
        comment: null,
        price: 0,
        size: 0,
        volume: 0,
        delta: 0,
        gamma: 0,
        vega: 0,
        theta: 0,
    });

    const handleOrderDetailsChange = (patch: Partial<OrderDetailsState>) => {
        setOrderDetails(prev => ({ ...prev, ...patch }));
    };
    
    useEffect(() => {
        if (editingModel === null || marketDataDto === undefined) {
            return;
        }

        const size = editingModel.size;
        
        if (assetService.isOptionType(editingModel.type)){
            const newContractDetails = {
                assetType: editingModel.type,
                expiration: dateTimeConversionService.convertDateToLongFormattedString(editingModel.expireAt),
                strike: editingModel.strike!,
                symbol: editingModel.symbol,
            };
            
            handleContractDetailsChange(newContractDetails);

            oderManagerUtils.updatePricingParameters(marketDataDto!, newContractDetails,
                dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                editingModel.impliedVolatilityForPricing ?? 0,
                editingModel.underlyingPriceForPricing ?? 0,
                handlePricingDetailsChange);
            
            handleOrderDetailsChange({
                externalId: editingModel.externalId,
                state: editingModel.state,
                isActive: editingModel.state === OrderState.Active || editingModel.state === OrderState.Archived,
                archived: editingModel.state === OrderState.Archived,
                executedAt: dateTimeConversionService.convertToUtcDate(editingModel.openedPositionAt),
                impliedVolatilityAtExecution: editingModel.impliedVolatilityAtOpen ?? 0,
                underlyingPriceAtExecution: editingModel.underlyingPriceAtOpen ?? 0,
                exchangeOrderId: editingModel.exchangeOrderId,
                comment: editingModel.comment,
                price: editingModel.openPrice,
                size: size,
                volume: -editingModel.openPrice * size,
                delta: (editingModel.delta ?? 0) * size,
                gamma: (editingModel.gamma ?? 0) * size,
                vega: (editingModel.vega ?? 0) * size,
                theta: (editingModel.theta ?? 0) * size,
            });
        } else {
            const newContractDetails = {
                assetType: editingModel.type,
                expiration: '',
                strike: null,
                symbol: editingModel.symbol,
            };
            
            handleContractDetailsChange(newContractDetails);

            oderManagerUtils.updatePricingParameters(marketDataDto!, newContractDetails,
                dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                0, editingModel.underlyingPriceForPricing ?? 0, handlePricingDetailsChange);
            
            handlePricingDetailsChange({
                dateForPricing: dateTimeConversionService.convertToUtcDate(editingModel.dateForPricing),
                impliedVolatilityForPricing: 0,
                underlyingPriceForPricing: editingModel.underlyingPriceForPricing ?? 0,
            });
            
            handleOrderDetailsChange({
                externalId: editingModel.externalId,
                state: editingModel.state,
                isActive: editingModel.state === OrderState.Active,
                archived: editingModel.state === OrderState.Archived,
                executedAt: dateTimeConversionService.convertToUtcDate(editingModel.openedPositionAt),
                underlyingPriceAtExecution: 0,
                impliedVolatilityAtExecution: 0,
                exchangeOrderId: editingModel.exchangeOrderId,
                comment: editingModel.comment,
                price: editingModel.openPrice,
                size: size,
                volume: -editingModel.openPrice * size,
                delta: 0,
                gamma: 0,
                vega: 0,
                theta: 0,
            });
        }
    }, [editingModel, marketDataDto]);
    
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleAutoOrderClick = (): void => {
        setIsAutoOrderManagerDialogOpen(true);
    };

    const fetchMarketInfo = async (): Promise<MarketDataDto | null> => {
        const selectedBaseAssetSymbol = localStorage.getItem(LocalStorageConstants.SelectedBaseAssetSymbol);
        const selectedExpiration = localStorage.getItem(LocalStorageConstants.SelectedExpirationName);

        return await fetchMarketData(selectedBaseAssetSymbol, selectedExpiration);
    }

    async function handleLoadExchangeData(dataProvider: DataProviders) {
        if (!isUserAuthenticated()) {
            navigate('/login');
            return;
        }

        setIsDataUpdating(true);
        try {
            setSelectedDataProvider(dataProvider);
            const updatedMarketData = await fetchMarketInfo();
            await reloadEditingOrder(updatedMarketData);
        } finally {
            setIsDataUpdating(false);
        }
    }

    const currentOptionInfo = marketDataDto?.OptionInfos.find(option => option.Symbol === contractDetails.symbol)
        ?? null;

    const getCurrentOptionImpliedVolatility = (): number | null =>
        currentOptionInfo?.MarkImpliedVolatility ?? null;

    const handleSubmit = async () => {
        if (editingModel === null || marketDataDto === undefined || submitAction === undefined) {
            return;
        }

        const portfolioItemUpdateModel: PortfolioItemUpdateModel = {
            externalId: editingModel.externalId,
            internalId: editingModel.internalId,
            exchangeOrderId: orderDetails.exchangeOrderId,
            autoOrderConfiguration: editingModel.autoOrderConfiguration,
            state: orderDetails.archived
                ? OrderState.Archived
                : orderDetails.isActive
                    ? OrderState.Active
                    : OrderState.Disabled,
            isActive: orderDetails.isActive,
            locked: orderDetails.archived,
            type: contractDetails.assetType,
            strike: assetService.isOptionType(contractDetails.assetType)
                ? contractDetails.strike
                : null,
            expireAt: assetService.isOptionType(contractDetails.assetType)
                ? dateTimeConversionService.parseToUtcDate(contractDetails.expiration)
                : null,
            symbol: contractDetails.symbol,
            size: orderDetails.size,
            openPrice: orderDetails.price ?? 0,
            markPrice: 0,
            dateForPricing: dateTimeConversionService.toUtcDate(pricingDetails.dateForPricing),
            impliedVolatilityForPricing: pricingDetails.impliedVolatilityForPricing,
            underlyingPriceForPricing: pricingDetails.underlyingPriceForPricing,
            openedPositionAt: dateTimeConversionService.toUtcDate(orderDetails.executedAt),
            underlyingPriceAtOpen: assetService.isOptionType(contractDetails.assetType)
                ? orderDetails.underlyingPriceAtExecution
                : null,
            impliedVolatilityAtOpen: assetService.isOptionType(contractDetails.assetType)
                ? orderDetails.impliedVolatilityAtExecution
                : null,
            delta: orderDetails.delta,
            gamma: null,
            vega: null,
            theta: null,
            comment: orderDetails.comment,
        };

        await submitAction(portfolioItemUpdateModel);
    }
    
    const handleSubmitAndCloseDialog = async (arg: React.FormEvent<HTMLFormElement>) => {
        arg.preventDefault();
        await handleSubmit();
        handleClose();
    };
    
    const handleDeleteClick = () => {
        setConfirmedAction(() => deleteAction());
        setConfirmationMessage("Are you sure you want to delete the item from portfolio?");
        setSubmitButtonName("Delete");
        setIsActionConfirmationDialogOpen(true);
    }
    
    const handleCommentChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        handleOrderDetailsChange({comment: arg.target.value});
    }

    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                confirmedParentAction={confirmedAction}
                submitButtonName={submitButtonName}
                doAfterConfirmation={handleClose}
            />
            <AutoOrderManagerDialog
                isOpen={isAutoOrderManagerDialogOpen}
                setIsOpen={setIsAutoOrderManagerDialogOpen}
                portfolioItemId={editingModel?.externalId ?? 0}
                optionOrderSymbol={contractDetails.symbol}
                optionOrderSize={orderDetails.size}
                optionImpliedVolatility={getCurrentOptionImpliedVolatility()}
                underlyingPrice={marketDataDto?.UnderlyingPrice ?? marketDataDto?.BaseAsset.Price ?? null}
                autoOrderConfiguration={editingModel?.autoOrderConfiguration ?? null}
                onConfigurationSaved={onAutoOrderConfigurationSaved}
                saveAutoOrderConfiguration={saveAutoOrderConfiguration}
                fetchAutoOrderConfiguration={fetchAutoOrderConfiguration}
                resetAutoOrderConfigurationState={resetAutoOrderConfigurationState}
                icon={<AutofpsSelectIcon />}
            />
            <Dialog
                open={isOpen}
                onClose={handleClose}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmitAndCloseDialog,
                }}
                maxWidth={false}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title={`${isNew ? "Add" : "Edit"} order: ${contractDetails.symbol}`}
                    onClose={handleClose}
                />
                <DialogContent sx={{paddingY: '11px', paddingX: '18px'}}>
                    <Stack direction="row" spacing={2}>
                        <Stack direction="column" spacing={1.7}>
                            <ContractDetails
                                contractDetails={contractDetails}
                                handleContractDetailsChange={handleContractDetailsChange}
                                orderDetails={orderDetails}
                                handleOrderDetailsChange={handleOrderDetailsChange}
                                handlePricingDetailsChange={handlePricingDetailsChange}
                            />
                            <PricingParameters
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                pricingDetails={pricingDetails}
                                handlePricingDetailsChange={handlePricingDetailsChange}
                            />
                            <OrderDetails
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                handleOrderDetailsChange={handleOrderDetailsChange}
                            />
                            <BinanceOrderManager
                                contractDetails={contractDetails}
                                orderDetails={orderDetails}
                                optionInfo={currentOptionInfo}
                                handleOrderDetailsChange={handleOrderDetailsChange}
                                handleSubmit={handleSubmit}
                                onAutoOrderClick={handleAutoOrderClick}
                            />
                            <TextField
                                margin="normal"
                                id="comment"
                                name="comment"
                                label="Comment"
                                type="string"
                                size="small"
                                value={orderDetails.comment ?? ''}
                                onChange={handleCommentChanging}
                                fullWidth
                                multiline
                                rows={1}
                                variant="outlined"
                            />
                        </Stack>
                        {assetService.isOptionType(contractDetails.assetType) &&
                            <OptionDeltaTrend
                                contractDetails={contractDetails}
                                pricingDetails={pricingDetails}
                            />
                        }
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Tooltip
                        title={`Update client data (Socket: ${marketDataDto?.ExchangeDataWebSocketState ?? 'N/A'})`}
                        slotProps={{ tooltip: { sx: { fontSize: 14 } } }}
                    >
                        <span>
                            <IconButton
                                type="button"
                                color="primary"
                                onClick={async () => await handleLoadExchangeData(DataProviders.Binance)}
                                disabled={isDataUpdating}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Stack direction="row" spacing={1}>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            size="small"
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={
                                orderDetails.size === null ||
                                orderDetails.price === null ||
                                (
                                    assetService.isOptionType(contractDetails.assetType) &&
                                    (contractDetails.expiration === '' ||
                                        contractDetails.strike === null)
                                )
                            }
                        >
                            {isNew ? "Add" : "Update"}
                        </Button>
                        <Button
                            sx={{ fontWeight: "bold" }}
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={handleDeleteClick}
                            disabled={
                                oderManagerUtils.isNewOrder(orderDetails) ||
                                oderManagerUtils.isOriginalOrderArchived(orderDetails) ||
                                oderManagerUtils.isOptionCanBeCancelled(orderDetails)
                            }
                        >
                            Delete
                        </Button>
                    </Stack>
                </DialogActions>
            </Dialog>
        </>
    );
}
